import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  WorkspacesOutlined as WorkspaceIcon,
  FolderOutlined as FolderIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import { fetchWorkspaces, moveContract } from '../../../redux/actions/contractAction';
import { backendClient } from '../../../api/backend';

const goldAccent = '#c9a96e';

const matchesSearch = (name, term) =>
  !term || (name || '').toLowerCase().includes(term.toLowerCase());

const FolderRow = ({
  folder,
  level,
  expanded,
  onToggle,
  selectedKey,
  onSelect,
  disabledKey,
  searchTerm,
  theme,
}) => {
  const key = `f:${folder.id}`;
  const isSelected = selectedKey === key;
  const isDisabled = disabledKey === key;
  const hasChildren = folder.children && folder.children.length > 0;
  const isOpen = expanded[folder.id];

  // If searching, recursively check if any descendant matches
  const childrenMatch = (f) => {
    if (matchesSearch(f.name, searchTerm)) return true;
    return (f.children || []).some(childrenMatch);
  };
  if (searchTerm && !childrenMatch(folder)) return null;

  return (
    <>
      <Box
        onClick={() => !isDisabled && onSelect(key, { workspace_id: folder.workspace_id, folder_id: folder.id })}
        sx={{
          display: 'flex',
          alignItems: 'center',
          pl: 2 + level * 2,
          pr: 1,
          py: 0.75,
          mx: 1,
          borderRadius: '6px',
          mb: 0.25,
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          opacity: isDisabled ? 0.45 : 1,
          backgroundColor: isSelected ? alpha(goldAccent, 0.16) : 'transparent',
          border: isSelected ? `1px solid ${alpha(goldAccent, 0.4)}` : '1px solid transparent',
          '&:hover': {
            backgroundColor: isDisabled
              ? 'transparent'
              : isSelected
              ? alpha(goldAccent, 0.22)
              : alpha(theme.palette.action.hover, 0.06),
          },
        }}
      >
        {hasChildren ? (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onToggle(folder.id);
            }}
            sx={{ p: 0, mr: 0.5 }}
          >
            {isOpen ? (
              <ExpandMoreIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
            ) : (
              <ChevronRightIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
            )}
          </IconButton>
        ) : (
          <Box sx={{ width: 20, mr: 0.5 }} />
        )}
        <FolderIcon
          sx={{
            fontSize: 16,
            mr: 1,
            color: isSelected ? goldAccent : theme.palette.text.secondary,
          }}
        />
        <Typography
          sx={{
            flex: 1,
            fontSize: '0.82rem',
            fontWeight: isSelected ? 600 : 400,
            color: isSelected ? goldAccent : theme.palette.text.primary,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {folder.name}
        </Typography>
        {isDisabled && (
          <Tooltip title="Current location">
            <CheckCircleIcon sx={{ fontSize: 14, color: theme.palette.text.disabled }} />
          </Tooltip>
        )}
      </Box>
      {hasChildren && isOpen && (
        <Box>
          {folder.children.map((child) => (
            <FolderRow
              key={child.id}
              folder={child}
              level={level + 1}
              expanded={expanded}
              onToggle={onToggle}
              selectedKey={selectedKey}
              onSelect={onSelect}
              disabledKey={disabledKey}
              searchTerm={searchTerm}
              theme={theme}
            />
          ))}
        </Box>
      )}
    </>
  );
};

const MoveContractDialog = ({
  open,
  onClose,
  contractIds = [],
  currentWorkspaceId = null,
  currentFolderId = null,
  onMoved,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const workspaces = useSelector((state) => state.contract?.workspaces || []);

  const [foldersByWs, setFoldersByWs] = useState({}); // workspaceId -> tree
  const [loadingWs, setLoadingWs] = useState({}); // workspaceId -> bool
  const [expandedWs, setExpandedWs] = useState({}); // workspaceId -> bool
  const [expandedFolders, setExpandedFolders] = useState({}); // folderId -> bool
  const [selectedKey, setSelectedKey] = useState(null);
  const [selectedTarget, setSelectedTarget] = useState(null); // { workspace_id, folder_id }
  const [searchTerm, setSearchTerm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const disabledKey = useMemo(() => {
    if (currentFolderId) return `f:${currentFolderId}`;
    if (currentWorkspaceId) return `w:${currentWorkspaceId}`;
    return 'none';
  }, [currentWorkspaceId, currentFolderId]);

  // Fetch workspaces on open
  useEffect(() => {
    if (open && workspaces.length === 0) {
      dispatch(fetchWorkspaces());
    }
  }, [open, workspaces.length, dispatch]);

  // Reset state on close
  useEffect(() => {
    if (!open) {
      setSelectedKey(null);
      setSelectedTarget(null);
      setSearchTerm('');
    }
  }, [open]);

  // Auto-expand current location's workspace
  useEffect(() => {
    if (open && currentWorkspaceId) {
      setExpandedWs((prev) => ({ ...prev, [currentWorkspaceId]: true }));
      loadFolders(currentWorkspaceId);
    }
  }, [open, currentWorkspaceId]);

  const loadFolders = async (workspaceId) => {
    if (foldersByWs[workspaceId] || loadingWs[workspaceId]) return;
    setLoadingWs((prev) => ({ ...prev, [workspaceId]: true }));
    try {
      const res = await backendClient.get(
        `api/contract/workspace/workspaces/${workspaceId}/folders`
      );
      const data = await res.json();
      setFoldersByWs((prev) => ({ ...prev, [workspaceId]: data || [] }));
    } catch (e) {
      console.error('Failed to fetch folders:', e);
      setFoldersByWs((prev) => ({ ...prev, [workspaceId]: [] }));
    }
    setLoadingWs((prev) => ({ ...prev, [workspaceId]: false }));
  };

  const handleToggleWorkspace = (workspaceId) => {
    const willExpand = !expandedWs[workspaceId];
    setExpandedWs((prev) => ({ ...prev, [workspaceId]: willExpand }));
    if (willExpand) loadFolders(workspaceId);
  };

  const handleSelect = (key, target) => {
    setSelectedKey(key);
    setSelectedTarget(target);
  };

  const handleConfirm = async () => {
    if (!selectedTarget || contractIds.length === 0) return;
    setSubmitting(true);
    const target = selectedTarget;
    const previous = { workspace_id: currentWorkspaceId, folder_id: currentFolderId };
    try {
      await Promise.all(
        contractIds.map((id) =>
          dispatch(moveContract(id, target.workspace_id, target.folder_id))
        )
      );

      const msg =
        contractIds.length === 1
          ? t('moveContract.successOne', 'Contract moved')
          : t('moveContract.successMany', '{{count}} contracts moved', { count: contractIds.length });

      enqueueSnackbar(msg, {
        variant: 'success',
        action: (snackId) => (
          <Button
            size="small"
            sx={{ color: '#fff' }}
            onClick={async () => {
              try {
                await Promise.all(
                  contractIds.map((id) =>
                    dispatch(moveContract(id, previous.workspace_id, previous.folder_id))
                  )
                );
                enqueueSnackbar(t('moveContract.undone', 'Move undone'), { variant: 'info' });
              } catch (e) {
                enqueueSnackbar(t('moveContract.undoError', 'Undo failed'), { variant: 'error' });
              }
            }}
          >
            {t('common.undo', 'Undo')}
          </Button>
        ),
      });

      if (onMoved) onMoved(target);
      onClose();
    } catch (e) {
      enqueueSnackbar(t('moveContract.error', 'Failed to move'), { variant: 'error' });
    }
    setSubmitting(false);
  };

  const filteredWorkspaces = workspaces.filter((w) => {
    if (!searchTerm) return true;
    if (matchesSearch(w.name, searchTerm)) return true;
    // workspace stays visible if any folder matches
    const tree = foldersByWs[w.id] || [];
    const anyFolderMatches = (folders) =>
      folders.some(
        (f) =>
          matchesSearch(f.name, searchTerm) || anyFolderMatches(f.children || [])
      );
    return anyFolderMatches(tree);
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { bgcolor: theme.palette.background.paper, borderRadius: '12px' },
      }}
    >
      <DialogTitle sx={{ fontSize: '1rem', fontWeight: 600 }}>
        {contractIds.length > 1
          ? t('moveContract.titleMany', 'Move {{count}} contracts to…', { count: contractIds.length })
          : t('moveContract.titleOne', 'Move contract to…')}
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ p: 2, pb: 1 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder={t('moveContract.search', 'Search workspaces and folders…')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box sx={{ maxHeight: 360, overflowY: 'auto', pb: 1 }}>
          {/* "No workspace" — root */}
          {!searchTerm && (
            <Box
              onClick={() => {
                if (disabledKey === 'none') return;
                handleSelect('none', { workspace_id: null, folder_id: null });
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 2,
                py: 0.85,
                mx: 1,
                borderRadius: '6px',
                cursor: disabledKey === 'none' ? 'not-allowed' : 'pointer',
                opacity: disabledKey === 'none' ? 0.45 : 1,
                backgroundColor: selectedKey === 'none' ? alpha(goldAccent, 0.16) : 'transparent',
                border:
                  selectedKey === 'none'
                    ? `1px solid ${alpha(goldAccent, 0.4)}`
                    : '1px solid transparent',
                mb: 0.5,
                '&:hover': {
                  backgroundColor:
                    disabledKey === 'none'
                      ? 'transparent'
                      : selectedKey === 'none'
                      ? alpha(goldAccent, 0.22)
                      : alpha(theme.palette.action.hover, 0.06),
                },
              }}
            >
              <BlockIcon sx={{ fontSize: 16, mr: 1, color: theme.palette.text.secondary }} />
              <Typography
                sx={{
                  fontSize: '0.82rem',
                  fontWeight: selectedKey === 'none' ? 600 : 400,
                  color: selectedKey === 'none' ? goldAccent : theme.palette.text.primary,
                  flex: 1,
                }}
              >
                {t('moveContract.noWorkspace', 'No workspace (unfile)')}
              </Typography>
              {disabledKey === 'none' && (
                <CheckCircleIcon sx={{ fontSize: 14, color: theme.palette.text.disabled }} />
              )}
            </Box>
          )}

          <Divider sx={{ mx: 2, my: 0.5 }} />

          {filteredWorkspaces.length === 0 && (
            <Box sx={{ px: 3, py: 4, textAlign: 'center' }}>
              <Typography sx={{ fontSize: '0.82rem', color: theme.palette.text.disabled }}>
                {workspaces.length === 0
                  ? t('moveContract.noWorkspaces', 'No workspaces yet')
                  : t('moveContract.noResults', 'No matches')}
              </Typography>
            </Box>
          )}

          {filteredWorkspaces.map((workspace) => {
            const wsKey = `w:${workspace.id}`;
            const isWsSelected = selectedKey === wsKey;
            const isWsDisabled = disabledKey === wsKey;
            const isExpanded = expandedWs[workspace.id] || !!searchTerm;
            const tree = foldersByWs[workspace.id];

            return (
              <Box key={workspace.id}>
                <Box
                  onClick={() =>
                    !isWsDisabled &&
                    handleSelect(wsKey, { workspace_id: workspace.id, folder_id: null })
                  }
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 1,
                    py: 0.85,
                    mx: 1,
                    borderRadius: '6px',
                    cursor: isWsDisabled ? 'not-allowed' : 'pointer',
                    opacity: isWsDisabled ? 0.45 : 1,
                    backgroundColor: isWsSelected ? alpha(goldAccent, 0.16) : 'transparent',
                    border: isWsSelected
                      ? `1px solid ${alpha(goldAccent, 0.4)}`
                      : '1px solid transparent',
                    '&:hover': {
                      backgroundColor: isWsDisabled
                        ? 'transparent'
                        : isWsSelected
                        ? alpha(goldAccent, 0.22)
                        : alpha(theme.palette.action.hover, 0.06),
                    },
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleWorkspace(workspace.id);
                    }}
                    sx={{ p: 0, mr: 0.5 }}
                  >
                    {isExpanded ? (
                      <ExpandMoreIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                    ) : (
                      <ChevronRightIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                    )}
                  </IconButton>
                  <WorkspaceIcon
                    sx={{
                      fontSize: 16,
                      mr: 1,
                      color: isWsSelected ? goldAccent : theme.palette.text.secondary,
                    }}
                  />
                  <Typography
                    sx={{
                      flex: 1,
                      fontSize: '0.82rem',
                      fontWeight: isWsSelected ? 600 : 500,
                      color: isWsSelected ? goldAccent : theme.palette.text.primary,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {workspace.name}
                  </Typography>
                  {isWsDisabled && (
                    <CheckCircleIcon sx={{ fontSize: 14, color: theme.palette.text.disabled }} />
                  )}
                </Box>

                {isExpanded && (
                  <Box>
                    {loadingWs[workspace.id] && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                        <CircularProgress size={16} sx={{ color: theme.palette.text.secondary }} />
                      </Box>
                    )}
                    {!loadingWs[workspace.id] && tree && tree.length === 0 && (
                      <Typography
                        sx={{
                          pl: 6,
                          py: 0.5,
                          fontSize: '0.75rem',
                          color: theme.palette.text.disabled,
                          fontStyle: 'italic',
                        }}
                      >
                        {t('moveContract.noFolders', 'No folders')}
                      </Typography>
                    )}
                    {tree &&
                      tree.map((folder) => (
                        <FolderRow
                          key={folder.id}
                          folder={folder}
                          level={1}
                          expanded={expandedFolders}
                          onToggle={(id) =>
                            setExpandedFolders((prev) => ({ ...prev, [id]: !prev[id] }))
                          }
                          selectedKey={selectedKey}
                          onSelect={handleSelect}
                          disabledKey={disabledKey}
                          searchTerm={searchTerm}
                          theme={theme}
                        />
                      ))}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={onClose} size="small" disabled={submitting}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          size="small"
          disabled={!selectedTarget || submitting}
          sx={{
            bgcolor: goldAccent,
            '&:hover': { bgcolor: alpha(goldAccent, 0.85) },
          }}
        >
          {submitting ? (
            <CircularProgress size={16} sx={{ color: '#fff' }} />
          ) : (
            t('moveContract.moveHere', 'Move here')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MoveContractDialog;
